/*
  K for Kaia search modal (snippets/search-drawer.liquid). drawer-component (global.js)
  opens and closes #Drawer-Search; this element runs what is inside it.

  Typing: 250 ms after the last key, Shopify predictive search renders
  sections/kfk-predictive-search.liquid for the term. Results are cached per term and a
  newer request cancels an older one, so a slow answer never overwrites a newer search.
  Loading: the spinner shows at once, the skeleton only if the answer takes over 200 ms.
  Error or timeout (8 s): the error card with Try again, the term stays in the bar.
  Long searches: no match for three or more words, so common words are dropped and the
  search narrows from the end ("pink flower girl dress for a winter wedding" becomes
  "pink flower girl"), up to four tries, with a note saying what was searched.
  Sold out only: similar dresses come from Shopify's product recommendations.
  Recent searches: the last five, kept in this browser only (localStorage).
*/
(() => {
  if (customElements.get("kfk-search")) return;

  const RECENT_KEY = "kfk_recent_searches";
  const RECENT_MAX = 5;
  const TIMEOUT_MS = 8000;
  const STOP = new Set(["a", "an", "and", "the", "for", "in", "on", "of", "to", "with", "my", "her", "our", "girls", "girl's", "kids", "size", "age", "years", "year", "old"]);

  const readRecent = () => {
    try {
      const v = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
      return Array.isArray(v) ? v.filter((x) => typeof x === "string").slice(0, RECENT_MAX) : [];
    } catch (e) {
      return [];
    }
  };
  const writeRecent = (list) => {
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) {}
  };

  class KfkSearch extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.drawer = this.closest("drawer-component");
      this.form = this.querySelector("[data-kfk-form]");
      this.input = this.querySelector("[data-kfk-input]");
      this.clearBtn = this.querySelector("[data-kfk-clear]");
      this.closeBtn = this.querySelector("[data-kfk-close]");
      this.spinner = this.querySelector("[data-kfk-spinner]");
      this.defaultEl = this.querySelector("[data-kfk-default]");
      this.resultsEl = this.querySelector("[data-kfk-results]");
      this.statusEl = this.querySelector("[data-kfk-status]");
      this.recentEl = this.querySelector("[data-kfk-recent]");
      this.recentList = this.querySelector("[data-kfk-recent-list]");
      this.tplLoading = this.querySelector("[data-kfk-tpl-loading]");
      this.tplError = this.querySelector("[data-kfk-tpl-error]");
      this.suggestUrl = this.dataset.suggestUrl;
      this.searchUrl = this.dataset.searchUrl;
      this.recommendUrl = this.dataset.recommendUrl;
      this.cache = new Map();
      this.token = 0;

      let timer;
      this.input.addEventListener("input", () => {
        const q = this.query();
        this.clearBtn.hidden = !q;
        clearTimeout(timer);
        if (!q) {
          this.cancel();
          this.showDefault();
          return;
        }
        timer = setTimeout(() => this.search(q), 250);
      });
      this.clearBtn.addEventListener("click", () => {
        this.input.value = "";
        this.clearBtn.hidden = true;
        this.cancel();
        this.showDefault();
        this.input.focus();
      });
      this.closeBtn.addEventListener("click", () => this.drawer && this.drawer.closeDrawer());
      this.form.addEventListener("submit", (e) => {
        const q = this.query();
        if (!q) {
          e.preventDefault();
          return;
        }
        this.remember(q);
      });
      // a click on a result remembers the term; a suggestion or the correction link
      // carries its own term, and the error card's Try again reruns the search
      this.addEventListener("click", (e) => {
        const retry = e.target.closest("[data-kfk-retry]");
        if (retry) {
          this.input.focus();
          this.search(this.query(), { fresh: true });
          return;
        }
        const link = e.target.closest("[data-kfk-results] a");
        if (link) this.remember(link.dataset.kfkTerm || this.lastTerm || this.query());
      });
      this.recentEl.addEventListener("click", (e) => {
        const remove = e.target.closest("[data-kfk-recent-remove]");
        if (remove) {
          writeRecent(readRecent().filter((x) => x !== remove.dataset.kfkRecentRemove));
          this.renderRecent();
          this.input.focus();
          return;
        }
        if (e.target.closest("[data-kfk-recent-clear]")) {
          writeRecent([]);
          this.renderRecent();
          this.input.focus();
        }
      });
      if (this.drawer) {
        this.drawer.addEventListener("open", () => {
          this.drawer.classList.add("kfk-search-drawer--used");
          this.renderRecent();
        });
        this.drawer.addEventListener("close", () => {
          this.input.value = "";
          this.clearBtn.hidden = true;
          this.cancel();
          this.showDefault();
        });
      }
      this.renderRecent();
    }

    query() {
      return this.input.value.replace(/\s+/g, " ").trim();
    }

    cancel() {
      this.token++;
      if (this.controller) this.controller.abort();
      clearTimeout(this.skeletonTimer);
      this.spinner.hidden = true;
    }

    showDefault() {
      this.resultsEl.hidden = true;
      this.resultsEl.innerHTML = "";
      this.defaultEl.hidden = false;
      this.lastTerm = "";
      this.renderRecent();
    }

    show(html) {
      this.defaultEl.hidden = true;
      this.resultsEl.innerHTML = html;
      this.resultsEl.hidden = false;
    }

    say(text) {
      this.statusEl.textContent = "";
      setTimeout(() => (this.statusEl.textContent = text), 60);
    }

    suggestFetchUrl(q) {
      const p = new URLSearchParams();
      p.set("q", q);
      p.set("resources[type]", "product,collection,query");
      p.set("resources[limit]", "6");
      p.set("resources[limit_scope]", "each");
      p.set("resources[options][unavailable_products]", "last");
      p.set("resources[options][fields]", "title,product_type,variants.title,tag");
      p.set("section_id", "kfk-predictive-search");
      return `${this.suggestUrl}?${p.toString()}`;
    }

    async fetchSection(url, signal) {
      if (this.cache.has(url)) return this.cache.get(url);
      const res = await fetch(url, { signal, credentials: "same-origin" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, "text/html");
      const root = doc.querySelector("[data-kfk-result]") || doc.querySelector("[data-kfk-similar-result]");
      const out = root ? root.outerHTML : "";
      this.cache.set(url, out);
      return out;
    }

    async search(q, opts = {}) {
      if (!q) return;
      this.cancel();
      const token = this.token;
      const ctrl = (this.controller = new AbortController());
      const signal = ctrl.signal;
      const killer = setTimeout(() => ctrl.abort("timeout"), TIMEOUT_MS);
      if (opts.fresh) this.cache.clear();
      this.spinner.hidden = false;
      this.skeletonTimer = setTimeout(() => {
        if (token === this.token) this.show(this.tplLoading.innerHTML);
      }, 200);

      try {
        const found = await this.resolve(q, signal, token);
        if (!found || token !== this.token) {
          clearTimeout(killer);
          return;
        }
        clearTimeout(this.skeletonTimer);
        clearTimeout(killer);
        this.spinner.hidden = true;
        this.lastTerm = found.used;
        this.show(found.html);
        if (found.corrected) this.addCorrectionNote(found.used, q);
        else if (found.used !== q) this.addNarrowNote(found.used);
        this.after(found.used);
      } catch (err) {
        clearTimeout(killer);
        if (token !== this.token) return;
        clearTimeout(this.skeletonTimer);
        this.spinner.hidden = true;
        this.show(this.tplError.innerHTML);
        this.say("Search is taking a little longer than usual. Try again.");
      }
    }

    // 1. predictive search, keeping only dresses that hold every typed word
    // 2. a spelling fix the section found ("sequen" -> "sequin")
    // 3. the full store search, which also finds sold out dresses
    // 4. a long search narrowed from the end
    async resolve(q, signal, token) {
      const first = await this.fetchSection(this.suggestFetchUrl(q), signal);
      if (token !== this.token) return null;
      if (this.count(first) > 0) return { html: first, used: q };
      const fix = this.attr(first, "data-correction");
      if (fix) {
        const h = await this.fetchSection(this.suggestFetchUrl(fix), signal);
        if (token !== this.token) return null;
        if (this.count(h) > 0) return { html: h, used: fix, corrected: true };
      }
      const full = await this.fetchSection(this.fullFetchUrl(q), signal);
      if (token !== this.token) return null;
      if (this.count(full) > 0) return { html: full, used: q };
      for (const shorter of this.narrow(q)) {
        const h = await this.fetchSection(this.suggestFetchUrl(shorter), signal);
        if (token !== this.token) return null;
        if (this.count(h) > 0) return { html: h, used: shorter };
      }
      return { html: first, used: q };
    }

    fullFetchUrl(q) {
      const p = new URLSearchParams();
      p.set("q", q);
      p.set("type", "product");
      p.set("options[prefix]", "last");
      p.set("section_id", "kfk-predictive-search");
      return `${this.searchUrl}?${p.toString()}`;
    }

    attr(html, name) {
      const m = html.match(new RegExp(`${name}="([^"]*)"`));
      if (!m) return "";
      const t = document.createElement("textarea");
      t.innerHTML = m[1];
      return t.value.trim();
    }

    addCorrectionNote(used, typed) {
      const link = (term, cls) => {
        const a = document.createElement("a");
        a.href = `${this.searchUrl}?options%5Bprefix%5D=last&type=product&q=${encodeURIComponent(term)}`;
        a.className = cls;
        a.dataset.kfkTerm = term;
        a.textContent = term;
        return a;
      };
      const note = document.createElement("p");
      note.className = "kfk-search__note";
      note.append("Showing results for ", link(used, "kfk-search__note-strong"), ". Search instead for ", link(typed, "kfk-search__note-link"));
      this.resultsEl.prepend(note);
    }

    count(html) {
      const m = html.match(/data-products="(\d+)"/);
      return m ? Number(m[1]) : 0;
    }

    // "pink flower girl dress for a winter wedding" -> ["pink flower girl dress winter", ..., "pink flower girl"]
    narrow(q) {
      const words = q.toLowerCase().split(" ");
      if (words.length < 3) return [];
      const kept = words.filter((w) => !STOP.has(w));
      const out = [];
      if (kept.length && kept.length < words.length) out.push(kept.join(" "));
      for (let n = kept.length - 1; n >= 1 && out.length < 4; n--) out.push(kept.slice(0, n).join(" "));
      return [...new Set(out)].filter((x) => x && x !== q.toLowerCase()).slice(0, 4);
    }

    addNarrowNote(used) {
      const note = document.createElement("p");
      note.className = "kfk-search__note";
      note.append("Nothing matches every word, so here are dresses for ");
      const b = document.createElement("strong");
      b.textContent = used;
      note.append(b, ".");
      this.resultsEl.prepend(note);
    }

    after(term) {
      const root = this.resultsEl.querySelector("[data-kfk-result]");
      if (!root) return;
      const products = Number(root.dataset.products || 0);
      const available = Number(root.dataset.available || 0);
      if (products === 0) this.say(`No dresses for ${term} just yet`);
      else if (available === 0) this.say(`${products} sold out ${products === 1 ? "dress" : "dresses"} for ${term}, with similar dresses below`);
      else this.say(`${products} ${products === 1 ? "dress" : "dresses"} for ${term}`);
      const similar = root.querySelector("[data-kfk-similar]");
      if (similar && similar.dataset.productId) this.loadSimilar(similar, similar.dataset.productId);
    }

    async loadSimilar(slot, productId) {
      const token = this.token;
      try {
        const url = `${this.recommendUrl}?product_id=${encodeURIComponent(productId)}&limit=8&section_id=kfk-search-similar`;
        const html = await this.fetchSection(url);
        if (token !== this.token || !html) return;
        slot.querySelector("[data-kfk-similar-list]").innerHTML = html;
        slot.hidden = false;
      } catch (e) {}
    }

    remember(q) {
      const term = (q || "").replace(/\s+/g, " ").trim();
      if (!term) return;
      writeRecent([term, ...readRecent().filter((x) => x.toLowerCase() !== term.toLowerCase())]);
    }

    renderRecent() {
      const list = readRecent();
      this.recentList.innerHTML = "";
      list.forEach((term) => {
        const li = document.createElement("li");
        li.className = "kfk-search__recent-item";
        const a = document.createElement("a");
        a.href = `${this.searchUrl}?options%5Bprefix%5D=last&type=product&q=${encodeURIComponent(term)}`;
        a.textContent = term;
        a.addEventListener("click", () => this.remember(term));
        const b = document.createElement("button");
        b.type = "button";
        b.className = "kfk-search__recent-remove";
        b.dataset.kfkRecentRemove = term;
        b.setAttribute("aria-label", `Remove ${term}`);
        b.innerHTML = '<svg aria-hidden="true" focusable="false" width="10" height="10" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M1 1l12 12M13 1L1 13"/></svg>';
        li.append(a, b);
        this.recentList.append(li);
      });
      this.recentEl.hidden = list.length === 0;
    }
  }

  customElements.define("kfk-search", KfkSearch);
})();
